import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getCreditInfoByStore,
  getCreditHistory,
  type CreditHistoryItem,
} from '../api/credit.api';
import Layout from '../components/layout/Layout';
import styles from './CreditHistoryPage.module.css';

/**
 * 포인트(크레딧) 사용 내역 페이지
 * KG이니시스 사이트 필수 구축사항:
 *   "포인트 사용 내역 화면 구축 (충전된 금액과 사용 금액 및 남은 금액 계산이 맞아야 함)"
 *
 * 표시 항목:
 *   - 잔여 크레딧 (모든 충전팩 합산)
 *   - 충전 이력 (충전 일시 / 종류 / 충전 / 사용 / 잔여 / 만료)
 *   - 합계 검증 (총 충전 - 총 사용 = 현재 잔여)
 */
const CREDIT_TYPE_LABEL: Record<string, string> = {
  SIGNUP_BONUS: '가입 보너스',
  PURCHASE: '카드 충전',
  ADMIN_GRANT: '관리자 지급',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function CreditHistoryPage() {
  const { storeId } = useAuth();
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [items, setItems] = useState<CreditHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!storeId) return;
    setIsLoading(true);
    setError(null);
    try {
      const info = await getCreditInfoByStore(storeId);
      setLicenseId(info.licenseId);
      setBalance(info.balance);
      const history = await getCreditHistory(info.licenseId);
      setItems(history);
    } catch (err) {
      setError(err instanceof Error ? err.message : '내역 조회 실패');
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 합계 계산 (KG이니시스 검증용 — 충전 - 사용 = 잔여)
  const totals = useMemo(() => {
    const charged = items.reduce((sum, it) => sum + it.totalCredits, 0);
    const used = items.reduce((sum, it) => sum + it.usedCredits, 0);
    const remaining = items.reduce((sum, it) => sum + it.remainingCredits, 0);
    return { charged, used, remaining };
  }, [items]);

  return (
    <Layout title="포인트 사용 내역">
      <div className={styles.page}>
        {/* 안내 박스 (KG이니시스 필수 고지 — 충전 화면과 동일 내용) */}
        <div className={styles.noticeBox}>
          <div className={styles.noticeTitle}>포인트(크레딧) 사용 안내</div>
          <ul className={styles.noticeList}>
            <li>충전된 포인트는 매장 키오스크 대기 등록 시 발송되는 카카오톡 알림톡 또는 SMS 1건당 1포인트씩 자동 차감됩니다.</li>
            <li>포인트 유효기간은 충전일로부터 1년(365일)이며, 경과 시 미사용분은 자동 소멸됩니다.</li>
            <li>충전 후 7일 이내, 미사용분에 한해 청약철회 및 환불이 가능합니다.</li>
            <li>환불은 결제하신 카드(또는 카카오페이) 등 원래 결제수단으로만 환급되며, 현금 환급은 불가합니다.</li>
          </ul>
        </div>

        {/* 합계 카드 */}
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>총 충전</div>
            <div className={styles.summaryValue}>
              {totals.charged.toLocaleString()}
              <span className={styles.unit}>건</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>총 사용</div>
            <div className={styles.summaryValue} style={{ color: '#dc2626' }}>
              {totals.used.toLocaleString()}
              <span className={styles.unit}>건</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>잔여 (총 충전 − 총 사용)</div>
            <div className={styles.summaryValue} style={{ color: 'var(--color-primary)' }}>
              {totals.remaining.toLocaleString()}
              <span className={styles.unit}>건</span>
            </div>
          </div>
        </div>

        {/* 검증 (계산 맞는지 표시 — KG이니시스 검수용) */}
        {balance !== null && (
          <div className={styles.verifyRow}>
            <span>잔액 검증:</span>
            <span>
              {totals.charged.toLocaleString()} − {totals.used.toLocaleString()} ={' '}
              <strong>{totals.remaining.toLocaleString()}건</strong>
            </span>
            {balance === totals.remaining ? (
              <span className={styles.verifyOk}>일치</span>
            ) : (
              <span className={styles.verifyFail}>불일치</span>
            )}
          </div>
        )}

        {error && <div className={styles.errorBox}>{error}</div>}

        {isLoading ? (
          <div className={styles.loading}>로딩 중...</div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>충전 이력이 없습니다.</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>충전 일시</th>
                  <th>종류</th>
                  <th style={{ textAlign: 'right' }}>충전</th>
                  <th style={{ textAlign: 'right' }}>사용</th>
                  <th style={{ textAlign: 'right' }}>잔여</th>
                  <th>만료일</th>
                  <th>비고</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDateTime(item.createdAt)}</td>
                    <td>
                      <span className={styles.typeBadge}>
                        {CREDIT_TYPE_LABEL[item.creditType] ?? item.creditType}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.totalCredits.toLocaleString()}건
                    </td>
                    <td style={{ textAlign: 'right', color: '#dc2626' }}>
                      {item.usedCredits.toLocaleString()}건
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {item.remainingCredits.toLocaleString()}건
                    </td>
                    <td>{formatDate(item.expiresAt)}</td>
                    <td>
                      {item.amount !== null && item.amount > 0
                        ? `${item.amount.toLocaleString()}원`
                        : item.memo || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {licenseId && (
          <div className={styles.licenseRef}>
            라이선스 ID: <code>{licenseId}</code>
          </div>
        )}
      </div>
    </Layout>
  );
}
